'use client'

import { create } from 'zustand'

import { toast } from '@/core/lib/toast'

import { api } from '@/features/veluvit/services/api'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

/**
 * 알림함 — 서버 `GET /notification/`(최근 100개 + 안 읽은 수) · `POST /notification/:id/`(읽음).
 * 서버가 만드는 알림: 보상(reward) · 결제(payment) · 환불(refund) · 작품 검수(creator) · 문의 답변(support) · 팔로우한 작가 소식.
 * 조회 실패는 알림함에서 안내한다.
 */
type NotificationItem = {
  id: string
  kind: string
  title: string
  data: Record<string, unknown>
  read: boolean
  createdAt: string
}

type NotificationState = {
  identity: number
  items: NotificationItem[]
  unread: number
  status: 'idle' | 'loading' | 'ready' | 'error'
}

export const useNotificationStore = create<NotificationState>()(() => ({
  identity: -1,
  items: [],
  unread: 0,
  status: 'idle',
}))

let loading: {
  identity: number
  fresh: boolean
  promise: Promise<void>
} | null = null
let queuedFresh: { identity: number; promise: Promise<void> } | null = null
const pendingReads = new Set<{ identity: number; promise: Promise<void> }>()
let readRevision = 0

export function syncNotificationIdentity() {
  const session = useSessionStore.getState()
  if (useNotificationStore.getState().identity !== session.identity) {
    useNotificationStore.setState({
      identity: session.identity,
      items: [],
      unread: 0,
      status: 'idle',
    })
  }
  return session.data?.user ? session.identity : null
}

async function fetchNotifications(identity: number) {
  const store = useNotificationStore
  store.setState({ status: 'loading' })
  try {
    // 읽음 POST와 겹친 GET만 한 번 재조회하고, 계속 겹치면 수동 재시도를 요청한다.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      await Promise.all(
        [...pendingReads]
          .filter((entry) => entry.identity === identity)
          .map((entry) => entry.promise)
      )
      const revision = readRevision
      const res = await api<{
        notifications: NotificationItem[]
        unread: number
      }>('/notification/')
      if (store.getState().identity !== identity) return
      if (
        revision !== readRevision ||
        [...pendingReads].some((entry) => entry.identity === identity)
      )
        continue
      store.setState({
        items: res.notifications,
        unread: res.unread,
        status: 'ready',
      })
      return
    }
    throw new Error('알림 상태가 계속 변경되고 있어요.')
  } catch {
    if (store.getState().identity === identity)
      store.setState({ status: 'error' })
  }
}

/** 목록 다시 받기 — 머리줄 아이콘이 둘(모바일 · PC) 떠 있어도 받는 중이면 한 번만 */
export function loadNotifications(fresh = false): Promise<void> {
  const identity = syncNotificationIdentity()
  if (identity === null) return Promise.resolve()
  if (!fresh && useNotificationStore.getState().status === 'ready')
    return Promise.resolve()
  if (loading?.identity === identity) {
    if (!fresh || loading.fresh) return loading.promise
    if (queuedFresh?.identity === identity) return queuedFresh.promise
    const promise = loading.promise
      .then(() => {
        if (syncNotificationIdentity() === identity)
          return loadNotifications(true)
      })
      .finally(() => {
        if (queuedFresh?.promise === promise) queuedFresh = null
      })
    queuedFresh = { identity, promise }
    return promise
  }
  const promise = fetchNotifications(identity).finally(() => {
    if (loading?.promise === promise) loading = null
  })
  loading = { identity, fresh, promise }
  return promise
}

/** 읽음 — 화면을 먼저 바꾸고, 실패하면 되돌린다(자동 재시도 없음) */
export async function markNotificationRead(id: string) {
  const identity = syncNotificationIdentity()
  if (identity === null) return
  const store = useNotificationStore
  const before = store.getState()
  const target = before.items.find((item) => item.id === id)
  if (!target || target.read) return
  readRevision += 1
  store.setState({
    items: before.items.map((item) =>
      item.id === id ? { ...item, read: true } : item
    ),
    unread: Math.max(0, before.unread - 1),
  })
  const pending = (async () => {
    try {
      await api(`/notification/${id}/`, {})
    } catch {
      if (store.getState().identity !== identity) return
      const now = store.getState()
      store.setState({
        items: now.items.map((item) =>
          item.id === id ? { ...item, read: false } : item
        ),
        unread: now.unread + 1,
      })
      toast.show('읽음으로 바꾸지 못했어요. 다시 눌러 주세요')
    } finally {
      if (store.getState().identity === identity) readRevision += 1
    }
  })()
  const entry = { identity, promise: pending }
  pendingReads.add(entry)
  await pending.finally(() => {
    pendingReads.delete(entry)
  })
}
